import { z } from 'zod';
import { SchemaTimestamp, ZodArrayToEnum } from '@axiom/types';
import {
  CandidateReputationActions,
  CandidateReputationDetailsSort,
} from '@axiom/const';

import { OpportunitySchema } from './opportunity';
import { UserSchema } from './user';
import { AccountSchema } from './account';

export const CandidateReputationSchema = z.object({
  averageClientRecommendScore: z.number().nonnegative().nullable(),
  businessJudgementScore: z.number().nonnegative().nullable(),
  candidateId: z.string().uuid().nullish(),
  communicationScore: z.number().nonnegative().nullable(),
  createdAt: SchemaTimestamp.nullable(),
  displayableSurveyScoreData: z.boolean().default(false),
  hasEnoughRespondents: z.boolean().default(false),
  id: z.string().uuid().nullish(),
  legalExpertiseScore: z.number().nonnegative().nullable(),
  numberOfAxiomEngagements: z.number().int().nonnegative().default(0),
  numberOfEngagements: z.number().int().nonnegative().default(0),
  numberOfRespondents: z.number().int().nonnegative().default(0),
  numberOfReviews: z.number().int().nonnegative().default(0),
  numberOfTestimonials: z.number().int().nonnegative().default(0),
  percentageOfReEngagements: z.number().nonnegative().nullable(),
  percentMetExpectations: z.number().nonnegative().nullable(),
  reputationSummary: z.string().nullable(),
  responsivenessScore: z.number().nonnegative().nullable(),
  talentRelatedRollOffs: z.number().int().nonnegative().default(0),
  updatedAt: SchemaTimestamp.nullable(),
  yearsAtAxiom: z.number().nonnegative().default(0),
});

export type CandidateReputation = z.infer<typeof CandidateReputationSchema>;

export const CandidateReputationDetailsSchema = z.object({
  id: z.string().uuid(),
  candidate: z.object({
    id: z.string().uuid(),
    calculatedDisplayName: z.string().nullable(),
    calculatedFirstName: z.string().nullable(),
    calculatedLastName: z.string().nullable(),
  }),
  opportunity: OpportunitySchema.pick({
    id: true,
    jobName: true,
  }).extend({
    ownerUser: UserSchema.pick({
      id: true,
      fullName: true,
    }).nullable(),
    salesLead: UserSchema.pick({
      id: true,
      fullName: true,
    }).nullable(),
    account: AccountSchema.pick({
      id: true,
      name: true,
    })
      .extend({
        ownerUser: UserSchema.pick({
          id: true,
          fullName: true,
        }).nullable(),
      })
      .nullable(),
  }),
  matterId: z.string().nullable(),
  clientRecommendScore: z.number().int().min(0).max(10).nullable(),
  communicationScore: z.number().min(1).max(5).nullable(),
  legalExpertiseScore: z.number().min(1).max(5).nullable(),
  businessJudgementScore: z.number().min(1).max(5).nullable(),
  responsivenessScore: z.number().min(1).max(5).nullable(),
  inTopTenPercent: z.boolean().nullable(),
  meetsExpectations: z.boolean().nullable(),
  didWellOn: z.string().nullable(),
  improveOn: z.string().nullable(),
  retiredDidWellOnImproveOn: z.string().nullable(),
  coachingAlert: z.boolean().nullable(),
  responseDate: SchemaTimestamp,
  respondentName: z.string().nullable(),
  axiomClientRecommendScore: z.number().int().nullable(),
  axiomDidWellOn: z.string().nullable(),
  axiomImproveOn: z.string().nullable(),
  historical: z.boolean().default(false),
  topTaskId: z.string().uuid().nullable(),
  status: z.string().nullable(),
  actionTakenAt: SchemaTimestamp.nullable(),
  actionTaken: z
    .enum(ZodArrayToEnum(Object.values(CandidateReputationActions)))
    .nullable(),
});

export type CandidateReputationDetails = z.infer<
  typeof CandidateReputationDetailsSchema
>;

export const CandidateReputationDetailsRequestBodySchema = z.object({
  sort: z.nativeEnum(CandidateReputationDetailsSort),
  search: z.string(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  order: z.enum(['desc', 'asc']),
});

export type CandidateReputationDetailsRequestBody = z.infer<
  typeof CandidateReputationDetailsRequestBodySchema
>;

export const CandidateReputationDetailsResponseBodySchema = z.object({
  data: z.array(CandidateReputationDetailsSchema),
  meta: z.object({
    resultCount: z.number().int().nonnegative(),
    currentPage: z.number().int().positive(),
    pageCount: z.number().int().nonnegative(),
  }),
});

export type CandidateReputationDetailsResponseBody = z.infer<
  typeof CandidateReputationDetailsResponseBodySchema
>;
